/**
 * ==========================================================================
 *
 * Scope: Foundation Phase
 *
 * Responsibility:
 * Maintains supported AI Widget Roles used by AI Layout Templates
 * and future AI-powered document enhancement features.
 *
 * ==========================================================================
 */

import React from "react";
import {
  ANIMATION,
  BRAND,
  CASESTUDY,
  CHART,
  COLLAGE,
  FRAME,
  ICON,
  ILLUSTRATION,
  ITINERARY,
  LINE,
  MAP,
  PICTURE,
  PORTFOLIO,
  PRICING_TABLE,
  QR_CODE,
  SCOPE_OF_WORK,
  SHAPE,
  STICKER,
  TABLE,
  TEXT,
  TEXT_FRAME,
  TIMELINE,
  UPLOAD,
  VIDEO,
} from "../../../constants/editor";

const widgetRoles = {
  text: [
    {
      group: "Primary",
      options: [
        {
          key: "heading",
          name: "Heading",
        },
        {
          key: "subheading",
          name: "Subheading",
        },
        {
          key: "body_text",
          name: "Body Text",
        },
        {
          key: "cta",
          name: "CTA",
        },
      ],
    },
    {
      group: "Highlight",
      options: [
        {
          key: "display",
          name: "Display",
        },
        {
          key: "stat",
          name: "Stat",
        },
        {
          key: "stat_label",
          name: "Stat Label",
        },
        {
          key: "quote",
          name: "Quote",
        },
      ],
    },
    {
      group: "Structure",
      options: [
        {
          key: "step_number",
          name: "Step Number",
        },
        {
          key: "eyebrow",
          name: "Eyebrow",
        },
        {
          key: "label",
          name: "Label",
        },
      ],
    },
    {
      group: "Supporting",
      options: [
        {
          key: "caption",
          name: "Caption",
        },
        {
          key: "meta",
          name: "Meta",
        },
        {
          key: "attribution",
          name: "Attribution",
        },
      ],
    },
    {
      group: "Utility",
      options: [
        {
          key: "page_number",
          name: "Page Number",
        },
      ],
    },
  ],

  image_and_graphic: [
    {
      group: "Primary",
      options: [
        {
          key: "hero_visual",
          name: "Hero Visual",
        },
        {
          key: "supporting_visual",
          name: "Supporting Visual",
        },
        {
          key: "background_visual",
          name: "Background Visual",
        },
      ],
    },
    {
      group: "Content",
      options: [
        {
          key: "thumbnail",
          name: "Thumbnail",
        },
        {
          key: "gallery_visual",
          name: "Gallery Visual",
        },
      ],
    },
    {
      group: "Brand",
      options: [
        {
          key: "logo",
          name: "Logo",
        },
        {
          key: "brand_asset",
          name: "Brand Asset",
        },
      ],
    },
    {
      group: "Supporting",
      options: [
        {
          key: "icon",
          name: "Icon",
        },
        {
          key: "badge",
          name: "Badge",
        },
        {
          key: "step_indicator",
          name: "Step Indicator",
        },
      ],
    },
  ],

  video: [
    {
      group: "Primary",
      options: [
        {
          key: "hero_video",
          name: "Hero Video",
        },
        {
          key: "supporting_video",
          name: "Supporting Video",
        },
        {
          key: "background_video",
          name: "Background Video",
        },
      ],
    },
  ],

  qr_code: [
    {
      group: "Functional",
      options: [
        {
          key: "qr_code",
          name: "QR Code",
        },
      ],
    },
  ],

  chart: [
    {
      group: "Data",
      options: [
        {
          key: "chart",
          name: "Chart",
        },
      ],
    },
  ],

  map: [
    {
      group: "Data",
      options: [
        {
          key: "map",
          name: "Map",
        },
      ],
    },
  ],

  table: [
    {
      group: "Data",
      options: [
        {
          key: "table",
          name: "Table",
        },
      ],
    },
  ],

  pricing_table: [
    {
      group: "Data",
      options: [
        {
          key: "pricing_table",
          name: "Pricing Table",
        },
      ],
    },
  ],

  timeline: [
    {
      group: "Data",
      options: [
        {
          key: "timeline",
          name: "Timeline",
        },
      ],
    },
  ],

  itinerary: [
    {
      group: "Data",
      options: [
        {
          key: "itinerary",
          name: "Itinerary",
        },
      ],
    },
  ],

  scope_of_work: [
    {
      group: "Content",
      options: [
        {
          key: "scope_of_work",
          name: "Scope of Work",
        },
      ],
    },
  ],

  case_study: [
    {
      group: "Content",
      options: [
        {
          key: "case_study",
          name: "Case Study",
        },
      ],
    },
  ],

  portfolio: [
    {
      group: "Content",
      options: [
        {
          key: "portfolio",
          name: "Portfolio",
        },
      ],
    },
  ],
};

const widgetFamilies = {
  text: [TEXT],
  image_and_graphic: [
    PICTURE,
    UPLOAD,
    BRAND,
    LINE,
    SHAPE,
    ICON,
    STICKER,
    ILLUSTRATION,
    ANIMATION,
    COLLAGE,
    FRAME,
    TEXT_FRAME,
  ],
  video: [VIDEO, UPLOAD, BRAND],
  qr_code: [QR_CODE],
  chart: [CHART],
  map: [MAP],
  table: [TABLE],
  pricing_table: [PRICING_TABLE],
  timeline: [TIMELINE],
  itinerary: [ITINERARY],
  scope_of_work: [SCOPE_OF_WORK],
  case_study: [CASESTUDY],
  portfolio: [PORTFOLIO],
};

export { widgetFamilies, widgetRoles };
